import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';

const GLOBAL_NAMESPACE = 'skykick';
const DEFAULT_TTL = 300_000;
class CacheNamespace {
    static root() {
        return new CacheNamespace(GLOBAL_NAMESPACE);
    }
    constructor(namespace) {
        this.namespace = namespace;
    }
    clear() {
        for (const skKey of Object.keys(localStorage).filter(x => x.startsWith(this.namespace))) {
            localStorage.removeItem(skKey);
        }
    }
    /**
     * Place the item in the cache, and return the item so this call can be chained
     * @template T the type of the value being cached.
     * @param key the key to identify the value.
     * @param value the object or value to store in the cache.
     * @param [ttl] the duration of the cache before expiry.
     * @returns The value being cached.
     */
    set(key, value, ttl = DEFAULT_TTL) {
        const record = {
            data: value,
            expiry: ttl >= 0 ? Date.now() + ttl : null,
        };
        localStorage.setItem(`${this.namespace}:${key}`, JSON.stringify(record));
        return value;
    }
    /**
     * Gets an item from the cache
     * @template T the type to coerce the item to.
     * @param key the key used to identity the item.
     * @returns The value stored or null if not found.
     */
    get(key) {
        // Get cached data from localStorage
        const item = localStorage.getItem(`${this.namespace}:${key}`);
        if (!item) {
            return null;
        }
        const record = JSON.parse(item);
        // if the data expired, remove it and return null
        if (record.expiry != null) {
            if (Date.now() > record.expiry) {
                this.remove(key);
                return null;
            }
        }
        return record.data;
    }
    /**
     * Return the item from the cache, or, if it doesn't exist,
     * create it and added it to the cache retrieving from the callback
     * @param key The key of the item in the cache
     * @param createCallback A callback function that will return a new item if needed
     */
    getOrCreate(key, createCallback, ttl = DEFAULT_TTL) {
        const cacheValue = this.get(key);
        return cacheValue || this.set(key, createCallback(), ttl);
    }
    /**
     * Removes item from cache
     * @param key the key to remove
     */
    remove(key) {
        localStorage.removeItem(`${this.namespace}:${key}`);
    }
    /**
     * Provides a new cache namespace scoped to the new child namespace provided.
     * @param ns the namespace to create
     * @returns a new namespace
     */
    withNamespace(ns) {
        return new CacheNamespace(`${this.namespace}:${ns}`);
    }
}

class CacheService {
    constructor() { }
    /**
     * Get the root "skykick" namespace
     * @returns The root namespace
     */
    root() {
        return CacheNamespace.root();
    }
    /**
     * Clear the root namespace which clears everything
     */
    clearAll() {
        CacheNamespace.root().clear();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CacheService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CacheService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CacheService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

var CacheExpiry;
(function (CacheExpiry) {
    CacheExpiry[CacheExpiry["Never"] = -1] = "Never";
})(CacheExpiry || (CacheExpiry = {}));

class CachingModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CachingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: CachingModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CachingModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CachingModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    exports: []
                }]
        }] });

/*
 * Public API Surface of caching
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CacheExpiry, CacheNamespace, CacheService, CachingModule, GLOBAL_NAMESPACE };
